import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../../services/api';
import CoinItem from '../../components/common/CoinItem';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorDisplay from '../../components/common/ErrorDisplay';
import EmptyState from '../../components/common/EmptyState';
import UserSearchDropdown from '../../components/features/UserSearchDropdown';
import BackButton from '../../components/common/BackButton';
import { formatRelativeTime } from '../../utils/dateUtils';
import { stripHTML } from '../../utils/htmlSanitizer';
import './SentCoins.css';

function SentCoins() {
  const auth = useAuth();
  const { userId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [allTransactions, setAllTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);

  // Filter states
  const [coinTypeFilter, setCoinTypeFilter] = useState('both'); // 'individual', 'organizational', 'both'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedRecipients, setSelectedRecipients] = useState([]); // Array of selected recipients
  const [descriptionSearch, setDescriptionSearch] = useState('');

  // Check if this is the current user's profile
  const isOwnProfile = auth.user?.profile?.sub === userId;

  // Apply URL query parameter for coin type filter
  useEffect(() => {
    const typeParam = searchParams.get('type');
    if (typeParam === 'individual' || typeParam === 'organizational') {
      setCoinTypeFilter(typeParam);
    }
  }, [searchParams]);

  // Fetch user and all transactions
  useEffect(() => {
    const fetchUserAndTransactions = async () => {
      if (!auth.isAuthenticated || !auth.user?.id_token) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const idToken = auth.user.id_token;

        // Fetch user profile and all sent transactions
        const [userData, transactionsData] = await Promise.all([
          apiService.getUserProfile(idToken, userId),
          apiService.getUserTransactionsById(idToken, userId, {
            type: 'sent',
            limit: 1000 // Get all transactions for filtering
          })
        ]);

        setUser(userData);
        // Only show completed (non-pending) transactions
        const completedTransactions = (transactionsData.transactions || []).filter(
          tx => !tx.pending
        );
        setAllTransactions(completedTransactions);
        setFilteredTransactions(completedTransactions);
      } catch (err) {
        console.error('Error fetching sent coins:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchUserAndTransactions();
    }
  }, [userId, auth.isAuthenticated, auth.user]);

  // Apply filters whenever filter criteria change
  useEffect(() => {
    let filtered = [...allTransactions];

    // Filter by coin type
    if (coinTypeFilter !== 'both') {
      filtered = filtered.filter(tx => tx.coin_type === coinTypeFilter);
    }

    // Filter by date range
    if (startDate) {
      const startDateTime = new Date(startDate).getTime();
      filtered = filtered.filter(tx => new Date(tx.created_at).getTime() >= startDateTime);
    }
    if (endDate) {
      // Add 1 day to include the entire end date
      const endDateTime = new Date(endDate).getTime() + 86400000;
      filtered = filtered.filter(tx => new Date(tx.created_at).getTime() < endDateTime);
    }

    // Filter by recipients (multiple)
    if (selectedRecipients.length > 0) {
      const recipientIds = selectedRecipients.map(r => r.user_id);
      filtered = filtered.filter(tx => recipientIds.includes(tx.receiver_id));
    }

    // Filter by description text
    if (descriptionSearch.trim()) {
      const searchLower = descriptionSearch.toLowerCase().trim();
      filtered = filtered.filter(tx => {
        const plainText = stripHTML(tx.description || '').toLowerCase();
        return plainText.includes(searchLower);
      });
    }

    setFilteredTransactions(filtered);
  }, [allTransactions, coinTypeFilter, startDate, endDate, selectedRecipients, descriptionSearch]);

  const handleRecipientSelect = (user) => {
    if (!user) return;

    // Check if recipient is already selected
    const isAlreadySelected = selectedRecipients.some(r => r.user_id === user.user_id);

    if (!isAlreadySelected) {
      setSelectedRecipients([...selectedRecipients, user]);
    }
  };

  const handleRemoveRecipient = (userId) => {
    setSelectedRecipients(selectedRecipients.filter(r => r.user_id !== userId));
  };

  const clearFilters = () => {
    setCoinTypeFilter('both');
    setStartDate('');
    setEndDate('');
    setSelectedRecipients([]);
    setDescriptionSearch('');
  };

  const hasActiveFilters = coinTypeFilter !== 'both' || startDate || endDate || selectedRecipients.length > 0 || descriptionSearch;

  if (loading) {
    return (
      <div className="sent-coins-container">
        <LoadingSpinner message="Loading coins..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="sent-coins-container">
        <ErrorDisplay
          title="Error Loading Coins"
          message={error}
          onRetry={() => window.history.back()}
        />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="sent-coins-container">
        <div className="error-container">
          <h2>User Not Found</h2>
          <p>The user you're looking for doesn't exist.</p>
          <BackButton label="Go Back" />
        </div>
      </div>
    );
  }

  // Get first name from display name
  const firstName = user.display_name.split(' ')[0];

  return (
    <div className="sent-coins-container">
      {/* Header */}
      <div className="sent-coins-header">
        <BackButton />
        <div className="header-info">
          <h1>{firstName}'s Sent Coins</h1>
          <p className="header-description">
            {filteredTransactions.length} {filteredTransactions.length === 1 ? 'coin' : 'coins'}
            {hasActiveFilters && ` (filtered from ${allTransactions.length} total)`}
          </p>
        </div>
      </div>

      {/* Filters Section */}
      <section className="filters-section">
        <h2 className="filters-title">Filters</h2>

        {/* First Row: Coin Type and Date Filters */}
        <div className="filters-grid">
          {/* Coin Type Filter - Organization option temporarily disabled */}
          {/* <div className="filter-group">
            <label htmlFor="coin-type-filter">Coin Type</label>
            <select
              id="coin-type-filter"
              value={coinTypeFilter}
              onChange={(e) => setCoinTypeFilter(e.target.value)}
              className="filter-select"
            >
              <option value="both">Both Types</option>
              <option value="individual">Individual Only</option>
              <option value="organizational">Organizational Only</option>
            </select>
          </div> */}

          {/* Start Date Filter */}
          <div className="filter-group">
            <label htmlFor="start-date-filter">From Date</label>
            <input
              type="date"
              id="start-date-filter"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="filter-input"
              max={endDate || undefined}
            />
          </div>

          {/* End Date Filter */}
          <div className="filter-group">
            <label htmlFor="end-date-filter">To Date</label>
            <input
              type="date"
              id="end-date-filter"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="filter-input"
              min={startDate || undefined}
            />
          </div>
        </div>

        {/* Second Row: Recipient Filter */}
        <div className="filters-grid filters-second-row">
          <div className="filter-group filter-group-wide">
            <label htmlFor="recipient-filter">Recipients</label>
            <UserSearchDropdown
              onSelect={handleRecipientSelect}
              placeholder="Search for recipients to add..."
              searchFn={async (query) => {
                const idToken = auth.user.id_token;
                return await apiService.searchUsers(idToken, query, 10);
              }}
            />
            {selectedRecipients.length > 0 && (
              <div className="selected-recipients-list">
                {selectedRecipients.map((recipient) => (
                  <div key={recipient.user_id} className="recipient-chip">
                    <span className="recipient-chip-name">{recipient.display_name}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveRecipient(recipient.user_id)}
                      className="recipient-chip-remove"
                      title={`Remove ${recipient.display_name}`}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Third Row: Description Search */}
        <div className="filters-grid filters-third-row">
          <div className="filter-group filter-group-wide">
            <label htmlFor="description-search">Search Description</label>
            <input
              type="text"
              id="description-search"
              value={descriptionSearch}
              onChange={(e) => setDescriptionSearch(e.target.value)}
              placeholder="Search in coin messages..."
              className="filter-input"
            />
          </div>
        </div>

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <div className="filters-actions">
            <button onClick={clearFilters} className="clear-filters-button">
              Clear All Filters
            </button>
          </div>
        )}
      </section>

      {/* Coins List */}
      <section className="coins-list-section">
        {filteredTransactions.length === 0 ? (
          <EmptyState
            icon={hasActiveFilters ? "🔍" : "🎉"}
            message={hasActiveFilters ? "No coins match your filters" : "No coins sent yet"}
          />
        ) : (
          <div className="coins-list">
            {filteredTransactions.map((tx) => (
              <CoinItem
                key={tx.tx_id}
                id={tx.tx_id}
                coinType={tx.coin_type}
                senderName={tx.receiver_name || 'Unknown'}
                senderId={tx.receiver_id}
                message={tx.description || 'No description'}
                timestamp={formatRelativeTime(tx.created_at)}
                senderEmail={tx.receiver_email}
                direction="sent"
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export default SentCoins;
